<template>
  <v-dialog v-model="open" max-width="520" content-class="upd-dialog">
    <div class="upd-root">

      <!-- Header -->
      <div class="upd-header">
        <div class="upd-header-icon">
          <i class="fal fa-hand-holding-usd"></i>
        </div>
        <div>
          <div class="upd-title">New upfront payment</div>
          <div class="upd-subtitle">Pay an influencer a fixed fee on top of commission</div>
        </div>
        <button class="upd-close" @click="close" :disabled="loading">
          <i class="fal fa-times"></i>
        </button>
      </div>

      <!-- Error -->
      <div v-if="apiErrors.length" class="upd-error">
        <i class="fal fa-exclamation-circle"></i>
        <div>
          <div v-for="(e, i) in apiErrors" :key="i">{{ e }}</div>
        </div>
      </div>

      <div class="upd-body">

        <!-- Step 1: Campaign -->
        <div class="upd-field-group">
          <label class="upd-label">Campaign</label>
          <campaign-autocomplete
            v-model="campaign"
            :label="null"
            :returnObject="true"
            :display-draft="false"
            outlined
            dense
            hide-details
          />
        </div>

        <!-- Step 2: Influencer -->
        <div class="upd-field-group">
          <label class="upd-label">Influencer</label>

          <div v-if="!campaign" class="upd-info-hint">
            <i class="fal fa-arrow-up"></i> Select a campaign first to load its influencers
          </div>

          <div v-else-if="influencersLoading" class="upd-loading">
            <v-progress-circular indeterminate color="#e05540" size="20" width="2"></v-progress-circular>
            <span>Loading influencers…</span>
          </div>

          <div v-else-if="campaignInfluencerItems.length === 0" class="upd-info-hint upd-info-hint--warn">
            <i class="fal fa-user-slash"></i> No active influencers on this campaign yet
          </div>

          <div v-else class="upd-influencer-list">
            <button
              v-for="inf in campaignInfluencerItems"
              :key="inf.uuid"
              class="upd-inf-row"
              :class="{ 'upd-inf-row--selected': selectedInfluencer && selectedInfluencer.uuid === inf.uuid }"
              @click="selectInfluencer(inf)"
            >
              <div class="upd-inf-avatar" :style="{ background: avatarColor(inf.full_name) }">
                {{ avatarInitials(inf.full_name) }}
              </div>
              <div class="upd-inf-name">{{ inf.full_name }}</div>
              <i v-if="selectedInfluencer && selectedInfluencer.uuid === inf.uuid" class="fal fa-check-circle upd-inf-check"></i>
            </button>
          </div>
        </div>

        <!-- Step 3: Amount -->
        <div class="upd-field-group">
          <label class="upd-label">Amount <span class="upd-label-hint">(excl. VAT, min. 500 kr)</span></label>
          <div class="upd-amount-wrap">
            <input
              v-model.number="amount"
              type="number"
              min="500"
              class="upd-amount-input"
              placeholder="0"
            />
            <span class="upd-amount-suffix">kr</span>
          </div>
          <div v-if="amount && amount < 500" class="upd-field-error">Minimum amount is 500 kr</div>
        </div>

      </div>

      <!-- Actions -->
      <div class="upd-actions">
        <button class="upd-btn upd-btn--ghost" @click="close" :disabled="loading">Cancel</button>
        <button class="upd-btn upd-btn--primary" :disabled="!canSubmit || loading" @click="submit">
          <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="white" class="mr-2"></v-progress-circular>
          <i v-else class="fal fa-check" style="margin-right:6px"></i>
          {{ loading ? 'Creating…' : 'Create payment' }}
        </button>
      </div>

    </div>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import CampaignAutocomplete from "@/components/business/autocomplete/CampaignAutocomplete.vue";

const AVATAR_COLORS = ["#4caf50", "#2196f3", "#e91e63", "#9c27b0", "#ff9800", "#00bcd4", "#795548", "#607d8b"];

export default {
  name: "UpfrontPaymentDialog",
  components: { CampaignAutocomplete },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      campaign: null,
      campaignInfluencerItems: [],
      influencersLoading: false,
      selectedInfluencer: null,
      amount: null,
      loading: false,
      apiErrors: [],
    };
  },
  computed: {
    open: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    canSubmit() {
      return this.campaign && this.selectedInfluencer && this.amount >= 500;
    },
  },
  watch: {
    campaign: {
      async handler(newCampaign) {
        this.selectedInfluencer = null;
        this.campaignInfluencerItems = [];
        if (!newCampaign?.uuid) return;
        await this.loadCampaignInfluencers(newCampaign.uuid);
      }
    }
  },
  methods: {
    ...mapActions("core", ["createUpfrontTransaction"]),
    ...mapActions("core/campaigns", ["getCampaignInfluencers"]),
    close() {
      this.open = false;
      this.reset();
    },
    reset() {
      this.campaign = null;
      this.campaignInfluencerItems = [];
      this.selectedInfluencer = null;
      this.amount = null;
      this.apiErrors = [];
      this.loading = false;
    },
    selectInfluencer(inf) {
      this.selectedInfluencer = inf;
    },
    async loadCampaignInfluencers(campaignUuid) {
      this.influencersLoading = true;
      try {
        const res = await this.getCampaignInfluencers({ campaign_uuid: campaignUuid, limit: 100, page: 1 });
        const rows = (res?.data || res?.response || res || []).filter(ci => ci.accepted_at && !ci.declined_at);
        const influencers = rows.map(row => row.influencer || row).filter(Boolean).map(inf => ({
          ...inf,
          full_name: inf?.user?.full_name || `${inf?.user?.first_name || ''} ${inf?.user?.last_name || ''}`.trim(),
        }));
        const seen = new Set();
        this.campaignInfluencerItems = influencers.filter(i => {
          if (!i.uuid || seen.has(i.uuid)) return false;
          seen.add(i.uuid);
          return true;
        });
      } catch {
        this.apiErrors = ["Could not load campaign influencers"];
      } finally {
        this.influencersLoading = false;
      }
    },
    async submit() {
      this.apiErrors = [];
      this.loading = true;
      try {
        await this.createUpfrontTransaction({
          campaign_id: this.campaign.uuid,
          influencer_id: this.selectedInfluencer.uuid,
          amount: Number(this.amount),
        });
        this.setSnackSuccess("Upfront payment created");
        this.$emit("created");
        this.close();
      } catch (e) {
        if (Array.isArray(e) && e[0]?.errors?.[0]?.message) {
          this.apiErrors = e.flatMap(x => x.errors.map(y => `${y.field}: ${y.message}`));
        } else {
          this.apiErrors = [e?.message || "Something went wrong"];
        }
      } finally {
        this.loading = false;
      }
    },
    avatarInitials(name) {
      return (name || '').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
    },
    avatarColor(name) {
      return AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];
    },
  },
};
</script>

<style lang="scss">
.upd-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
$accent: #e05540;

.upd-root {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
}

/* Header */
.upd-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.upd-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #fff0ee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: $accent;
  flex-shrink: 0;
}

.upd-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.upd-subtitle {
  font-size: 12px;
  color: #aaa;
  margin-top: 2px;
}

.upd-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f5f5f5;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  font-size: 13px;
  transition: background 0.15s;
  &:hover { background: #eee; color: #333; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

/* Error */
.upd-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 20px 0;
  background: #fff5f5;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #c62828;

  i { margin-top: 1px; flex-shrink: 0; }
}

/* Body */
.upd-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upd-field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upd-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.02em;
}

.upd-label-hint {
  font-weight: 400;
  color: #aaa;
}

/* Hints */
.upd-info-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #aaa;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px 14px;

  &--warn { color: #f57f17; background: #fff8e1; border-color: #ffe082; }
}

.upd-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #aaa;
  padding: 12px 0;
}

/* Influencer list */
.upd-influencer-list {
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.upd-inf-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 14px;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f8f8f8;
  cursor: pointer;
  transition: background 0.12s;
  text-align: left;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }

  &--selected {
    background: #fff8f7;
    .upd-inf-name { color: $accent; font-weight: 600; }
  }
}

.upd-inf-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.upd-inf-name {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.upd-inf-check {
  font-size: 16px;
  color: $accent;
}

/* Amount */
.upd-amount-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within { border-color: $accent; }
}

.upd-amount-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 11px 14px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  background: transparent;

  &::placeholder { color: #ccc; font-weight: 400; }
}

.upd-amount-suffix {
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
  color: #aaa;
  background: #fafafa;
  border-left: 1px solid #f0f0f0;
  align-self: stretch;
  display: flex;
  align-items: center;
}

.upd-field-error {
  font-size: 12px;
  color: #c62828;
  margin-top: 2px;
}

/* Actions */
.upd-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
}

.upd-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s;

  &:disabled { opacity: 0.45; cursor: not-allowed; }

  &--ghost {
    background: transparent;
    color: #888;
    border: 1px solid #e0e0e0;
    &:hover:not(:disabled) { background: #f5f5f5; color: #333; }
  }

  &--primary {
    background: $accent;
    color: #fff;
    &:hover:not(:disabled) { background: #c94830; }
  }
}
</style>